import { getCornerRadius } from "@swiggy-private/dls-web-common/dist/components/themes/CornerRadiusMapping/mapping";
import styled, { DefaultTheme, keyframes } from "styled-components";
import { IAddButtonProps, IAddButtonSize, ICounterDirection, ADD_BUTTON_VARIANT } from "./types";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types";

export const StyledAddButtonOuterContainer = styled("div")({
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    alignItems: "center",
});
export const getButtonWidth = (size: IAddButtonSize): string => {
    if (size === "normal") {
        return "120px";
    } else if (size === "small") {
        return "76px";
    } else {
        return "64px";
    }
};
export const getButtonHeight = (size: IAddButtonSize): string => {
    if (size === "normal") {
        return "40px";
    } else if (size === "small") {
        return "32px";
    } else {
        return "28px";
    }
};

export const getNegativeButtonHeight = (size: IAddButtonSize) => `-${getButtonHeight(size)}`;

const getBorder = ({
    isUnavailable = false,
    isIncrementDisabled = false,
    addButtonVariant,
    theme,
    customBorderColor,
}: {
    isUnavailable?: boolean;
    addButtonVariant: ADD_BUTTON_VARIANT;
    isIncrementDisabled?: boolean;
    theme: DefaultTheme;
    customBorderColor?: keyof IColors;
}) => {
    const BORDER_CONFIG = {
        [ADD_BUTTON_VARIANT.TOING]: {
            width: "1.5px",
            borderColor: "black",
        },
        [ADD_BUTTON_VARIANT.POP]: {
            width: "1px",
            borderColor: "black",
        },
        [ADD_BUTTON_VARIANT.DEFAULT]: {
            width: "1px",
            borderColor: theme.colors[theme.colorMode].border_Secondary,
        },
    };

    const config = BORDER_CONFIG[addButtonVariant];
    const borderWidth = isUnavailable ? "1px" : config.width;

    let borderColor;
    if (isUnavailable) {
        borderColor = theme.colors[theme.colorMode].border_Secondary;
    } else if (customBorderColor) {
        borderColor = theme.colors[theme.colorMode][customBorderColor as keyof IColors];
    } else if (isIncrementDisabled) {
        borderColor = theme.colors[theme.colorMode].warning;
    } else {
        borderColor = config.borderColor;
    }

    return `${borderWidth} solid ${borderColor}`;
};

const getBoxShadow = (isUnavailable: boolean, disableBoxShadow: boolean, addButtonVariant: ADD_BUTTON_VARIANT) => {
    if (isUnavailable || disableBoxShadow) {
        return "none";
    }
    if (addButtonVariant === ADD_BUTTON_VARIANT.POP) {
        return "2px 2px 0px 0px #000000";
    }
    return "0px 3px 8px rgba(40, 44, 63, 0.08)";
};

export const StyledAddButtonInnerContainer = styled("div")<{
    size: IAddButtonSize;
    isUnavailable?: boolean | undefined;
    disableBoxShadow: boolean;
    addButtonVariant: ADD_BUTTON_VARIANT;
    isIncrementDisabled?: boolean;
    customBorderColor?: keyof IColors;
}>(({ theme, size, isUnavailable, disableBoxShadow, addButtonVariant, isIncrementDisabled, customBorderColor }) => ({
    background: theme.colors[theme.colorMode].background_Add_Button,
    border: getBorder({
        isUnavailable,
        addButtonVariant,
        theme,
        isIncrementDisabled,
        customBorderColor,
    }),

    height: getButtonHeight(size),
    borderRadius: getCornerRadius(theme, "xSmall"),
    width: getButtonWidth(size),
    position: "relative",
    overflow: "hidden",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "space-between",
    boxShadow: getBoxShadow(!!isUnavailable, disableBoxShadow, addButtonVariant),
}));

const StyledCommonButton = styled("button")<Pick<IAddButtonProps, "isUnavailable">>(({ isUnavailable }) => ({
    "-webkit-appearance": "none",
    "-moz-appearance": "none",
    background: "transparent",
    boxShadow: "none",
    border: "none",
    height: "100%",
    cursor: isUnavailable ? "not-allowed" : "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textTransform: isUnavailable ? "none" : "uppercase",

    "&:focus": {
        outline: "none",
        background: "none",
    },
}));

export interface ICenterButton {
    isActive: boolean;
    size: IAddButtonSize;
}

export const StyledCenterButton = styled(StyledCommonButton)<ICenterButton>(
    ({ theme, isActive, size, isUnavailable }) => ({
        ...(isActive
            ? {
                  width: getButtonWidth(size),
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
              }
            : {
                  width: "52px",
              }),
        ...(isUnavailable
            ? {
                  padding: `0 ${theme.spacing.xxSmall}`,
              }
            : {}),

        transition: "all 100ms ease-in-out",
        transform: `translateY(${isActive ? 0 : getNegativeButtonHeight(size)})`,
        textAlign: "center",
    }),
);

export const IconDownChevron = styled("div")(({ theme }) => ({
    marginLeft: theme.spacing.xxSmall,
}));

export interface ISideButtons {
    isActive: boolean;
    size: IAddButtonSize;
}

export const StyledLeftButton = styled(StyledCommonButton)<ISideButtons>(({ theme, isActive, size }) => ({
    zIndex: 1,
    paddingLeft: size === "normal" ? theme.spacing.medium : theme.spacing.xSmall,
    paddingRight: size === "normal" ? theme.spacing.small : theme.spacing.xSmall,
    transition: "all 100ms ease-in-out",
    transform: `translateX(${isActive ? 0 : "-64px"})`,

    //required for central alignment
    paddingBottom: "1px",
}));

export const StyledRightButton = styled(StyledCommonButton)<ISideButtons>(({ theme, isActive, size }) => ({
    paddingRight: size === "normal" ? theme.spacing.medium : theme.spacing.xSmall,
    paddingLeft: size === "normal" ? theme.spacing.small : theme.spacing.xSmall,
    transition: "all 100ms ease-in-out",
    transform: `translateX(${isActive ? 0 : "64px"})`,

    //required for central alignment
    paddingTop: "1px",
}));

export interface IVerticalValues {
    isActive: boolean;
    size: IAddButtonSize;
}

export const StyledTopValue = styled(StyledCommonButton)<IVerticalValues>(({ isActive, size }) => ({
    transition: "all 100ms ease-in-out",
    height: getButtonHeight(size),
    cursor: "none",
    pointerEvents: "none",
    transform: `translateY(${isActive ? 0 : getNegativeButtonHeight(size)})`,
    position: "absolute",
    width: "100%",
}));

export const StyledCenterStepper = styled("div")({
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
});

export interface IStyledCenterValue {
    direction: ICounterDirection;
    size: IAddButtonSize;
}

export const StyledCenterValue = styled(StyledCommonButton)<IStyledCenterValue>(() => {
    return {
        transition: "none",
        height: "100%",
        textAlign: "center",
        cursor: "none",
        pointerEvents: "none",
        position: "absolute",
        width: "100%",
    };
});

export const StyledBottomValue = styled(StyledCommonButton)<IVerticalValues>(({ isActive, size }) => ({
    transition: "all 100ms ease-in-out",
    height: getButtonHeight(size),
    cursor: "none",
    pointerEvents: "none",
    transform: `translateY(${isActive ? 0 : getButtonHeight(size)})`,
    position: "absolute",
    width: "100%",
}));

export const StyledAddButtonHelperText = styled("div")(({ theme }) => ({
    marginTop: theme.spacing.xxxSmall,
}));

export const createLoaderAnimation = (buttonWidth: string) => keyframes`
  50% {
    transform: translate3d(-${parseFloat(buttonWidth) - parseFloat(buttonWidth) / 4 + 1}px, 0, 0);
  }
  to {
    transform: translateZ(0);
  }
`;

export const Loader = styled.div<{ size: IAddButtonSize }>`
    position: absolute;
    width: ${({ theme }) => theme.spacing.medium};
    height: ${({ theme }) => theme.spacing.xxxSmall};
    background-color: ${({ theme }) => theme.colors[theme.colorMode].special_positive};
    right: 0;
    bottom: 0;
    animation: ${({ size }) => createLoaderAnimation(getButtonWidth(size))} 1s ease infinite;
`;
